import * as React from 'react';
import { useContext, useRef } from 'react';

type OverflowCarouselContextType = {
  registerItem?: (id: string, node: HTMLDivElement) => () => void;
  getItemsOffset?: () => Map<string, { offsetLeft: number; offsetWidth: number }>;
  getItem?: (id: string) => HTMLDivElement | undefined;
};

const OverflowCarouselContext = React.createContext<OverflowCarouselContextType>({});

const OverflowCarouselProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const itemsRef = useRef<Map<string, HTMLDivElement> | null>(null);

  function getMap() {
    if (!itemsRef.current) {
      // Initialize the Map on first usage.
      itemsRef.current = new Map();
    }
    return itemsRef.current;
  }

  function registerItem(id: string, node: HTMLDivElement) {
    const map = getMap();
    map.set(id, node);

    return () => {
      map.delete(id);
    };
  }

  const getItemsOffset = () => {
    const map = getMap();
    const itemsOffset = new Map<string, { offsetLeft: number; offsetWidth: number }>();

    map.forEach((element, id) => {
      itemsOffset.set(id, { offsetLeft: element.offsetLeft, offsetWidth: element.offsetWidth });
    });

    return itemsOffset;
  };

  const getItem = (id: string) => {
    const map = getMap();
    return map.get(id);
  };

  return (
    <OverflowCarouselContext.Provider
      value={{
        registerItem,
        getItemsOffset,
        getItem,
      }}
    >
      {children}
    </OverflowCarouselContext.Provider>
  );
};

export const useRegisterItem = () => {
  const { registerItem } = useContext(OverflowCarouselContext);

  return registerItem;
};

export const useItemsOffset = () => {
  const { getItemsOffset } = useContext(OverflowCarouselContext);

  return getItemsOffset;
};

export const useGetItem = () => {
  const { getItem } = useContext(OverflowCarouselContext);

  return getItem;
};

export default OverflowCarouselProvider;
